import { useAtomValue } from 'jotai'
import { isNil } from 'ramda'

import type {
  ArticleButtonComponent,
  ArticleIconButtonComponent,
  LinksButtonComponent,
  LinksLinkComponent,
  LinksSimpleLinkComponent,
  Navigation,
  NavigationLinkwithiconComponent,
} from '@twisto/api-cms'
import { useBreakpoint } from '@twisto/hooks'

import {
  Button as ArticleButton,
  BaseLink,
  IconButton,
} from 'homepage/components/atoms'

import { navigationThemeAtom } from '../../navigation-theme'
import { secondaryButton } from '../secondary-navigation-slot.styles'

import { Button, Link, LinkWithIcon } from './'

type Props = {
  secondaryNavigationContent: Navigation['secondary']
  hideOnMobile?: boolean
  increaseFontSize?: boolean
}

type LinkComponent =
  | (LinksLinkComponent & { __component: 'links.link' })
  | (LinksButtonComponent & { __component: 'links.button' })
  | (LinksSimpleLinkComponent & { __component: 'links.simple-link' })
  | (ArticleButtonComponent & {
      __component: 'article.button'
    })
  | (ArticleIconButtonComponent & {
      __component: 'article.icon-button'
    })
  | (NavigationLinkwithiconComponent & {
      __component: 'navigation.linkwithicon'
    })

export const SecondaryComponents = ({
  secondaryNavigationContent,
  hideOnMobile = false,
  increaseFontSize,
}: Props) => {
  const mobileUp = useBreakpoint('smUp')
  const shouldDisplay = hideOnMobile ? mobileUp : true
  const navigationTheme = useAtomValue(navigationThemeAtom)

  if (isNil(secondaryNavigationContent)) {
    return null
  }

  const componentsArray = secondaryNavigationContent.map((component, index) => {
    const linkComponent = component as LinkComponent

    switch (linkComponent.__component) {
      case shouldDisplay && 'navigation.linkwithicon':
        return (
          <LinkWithIcon
            key={index}
            increaseFontSize={increaseFontSize}
            link={linkComponent}
          />
        )
      case shouldDisplay && 'links.link':
        return (
          <Link
            key={index}
            increaseFontSize={increaseFontSize}
            link={linkComponent as LinksLinkComponent}
          />
        )
      case shouldDisplay && 'links.simple-link':
        return (
          <BaseLink
            key={index}
            fontWeight="medium"
            hover="primary"
            inverted={navigationTheme === 'dark'}
            variant="primary"
          />
        )
      case 'links.button':
        return <Button key={index} increaseFontSize link={linkComponent} />
      case 'article.button':
        return <ArticleButton key={index} styles={secondaryButton} />
      case shouldDisplay && 'article.icon-button':
        return <IconButton key={index} styles={secondaryButton} />
      default:
        return null
    }
  })

  return <>{componentsArray}</>
}
